/**
 * @author       grady@zumper.com (Grady Kuhnline)
 * @copyright    Copyright (c) 2018, Zumper
 * @description  Check if a box or center is similar. Useful for preventing unnecessary updates
 */

import type { MaybeEmpty } from '@zcode/utility-types'
import { isNumber } from '@zcode/utils'
import type { CoordsArr, MaybeNullBoundsObj, MaybeNullCoords } from '../../types'

type CoordsVal = MaybeNullCoords | CoordsArr

export const similar = (a: number, b: number, precision = 2) => {
  if ((!a && !b) || a === b) {
    return true
  }
  if ((a && !b) || (!a && b) || !isNumber(a) || !isNumber(b)) {
    return false
  }

  const diffRange = Math.pow(10, precision * -1)
  const diff = Math.abs(a - b)
  if (diff < diffRange) {
    return true
  }

  return false
}

// precision of 2 is roughly 1 km
// https://en.wikipedia.org/wiki/Decimal_degrees#Precision
export const similarValues = (
  a: MaybeEmpty<MaybeNullBoundsObj> | CoordsVal | null,
  b: MaybeEmpty<MaybeNullBoundsObj> | CoordsVal | null,
  precision = 2
) => {
  if ((!a && !b) || a === b) {
    return true
  }
  if (!a || !b) {
    return false
  }
  const keys =
    Array.isArray(a) && typeof a.keys === 'function' ? Array.from(a.keys()) : Object.keys(a)

  // @ts-expect-error
  return keys.every((key: number | string) => similar(a[key], b[key], precision))
}
